import { Auth0VueClient, useAuth0 } from '@auth0/auth0-vue';
import axios from 'axios';
import { sendError } from '../plugins/sentry';
import { Opt, OptType } from '/@types/opt';

export function logout() {
  // logout logic should be handled by Auth0 plugin in components
}

export function createAxiosClient(auth0: Auth0VueClient) {
  const http = axios.create({
    baseURL: import.meta.env.VITE_API_BASEURL as string,
  });

  http.interceptors.request.use(async (config) => {
    const { getAccessTokenSilently } = useAuth0();

    // Only attach token for your API calls (optional but recommended)
    // if (!config.url?.startsWith("/")) return config

    // if (auth0.isLoading) return config;

    const token = await auth0.getAccessTokenSilently({
      // If you need to override/ensure audience/scope per call, do it here:
      // authorizationParams: { audience: "https://api.example.com", scope: "read:foo" },
    });

    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  return http;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASEURL as string,
});

// api.interceptors.request.use(
//   async (config) => {
//     // const accessToken = await getAccessToken();
//     const accessToken = getAccessTokenSilently();
//     config.headers['Authorization'] = `Bearer ${accessToken}`;
//     return config;
//   },
//   (error) => {
//     // Do something with request error
//     return Promise.reject(error);
//   },
// );

// api.interceptors.request.use(async (config) => {
//   try {
//     const { getAccessTokenSilently } = useAuth0();

//     const accessToken = await getAccessTokenSilently({
//       // Optional: Specify the audience and scopes required by your API
//       // audience: "YOUR_API_IDENTIFIER",
//       // scope: "read:data write:data",
//     });

//     // Use the access token to make an authenticated request
//     if (config.headers != null) {
//       config.headers['Authorization'] = `Bearer ${await accessToken}`;
//     }
//   } catch (error) {
//     console.error(error);
//   }

//   return config;
// });

export function createQueryParams(properties: Map<string, any>) {
  const query = new URLSearchParams();

  [...properties].forEach(([name, value]) => {
    if (value == null || (typeof value === 'string' && value.length === 0)) return;

    if (typeof value === 'object') {
      [...value].forEach((val) => {
        if (!val) return;
        query.append(name, val);
      });
    } else {
      query.append(name, value);
    }
  });
  return query;
}

export function filterQuery(opt: Opt, columns: Array<any /*Column*/>): URLSearchParams {
  const searchParams = new URLSearchParams();
  const nFilters = opt.filters.filter(({ columnId }) => typeof columnId === 'number');
  const sFilters = opt.filters.filter(({ columnId }) => typeof columnId !== 'number');

  if (opt.type == OptType.List) {
    searchParams.append('limit', String(opt.limit));
    searchParams.append('offset', String(opt.offset));
    searchParams.append('orderBy', String(opt.sortBy));
    searchParams.append('orderDesc', String(opt.sortDesc));
  }

  if (opt.type === OptType.Map) {
    searchParams.append('limit', String(100_000)); // 100_000 is max limit from API
  }

  if (opt.search.length > 0) {
    searchParams.append('search', String(opt.search));
  }

  if (opt.tenantId != null) {
    searchParams.append('partnerId', String(opt.tenantId));
  }

  if (nFilters.length > 0) {
    const base64 = btoa(
      JSON.stringify(
        nFilters.map(({ columnId, values }) => {
          const column = columns.find(({ id }) => id === columnId);
          return {
            id: column.filterKey,
            values:
              ('filterList' in column || 'getFilterList' in column) != null
                ? values
                : values.map((value) => ([-100, '#EMPTY'].includes(value) ? value : `%${value}%`)),
          };
        }),
      ),
    );
    searchParams.append('pFilterBase64', base64);
  }

  sFilters.forEach(({ values, columnId }) => {
    const column = columns.find(({ id }) => id === columnId);
    if (column) {
      values.forEach((value) => searchParams.append(column.filterKey, String(value)));
    }
  });

  if (opt.filters2.length > 0) {
    opt.filters2.forEach((f, index) => {
      const prefix = `Prop[${index}]`;
      searchParams.append(prefix + '.Id', String(f.columnId));
      searchParams.append(prefix + '.Filter', f.type);
      searchParams.append(prefix + '.Value', f.value);
    });
  }

  return searchParams;
}

export function genericApiErrorHandler(error: any, custom = null): Promise<any> {
  sendError(error, {
    requestId: error?.response?.headers?.requestid,
    requestMethod: error?.config?.method,
    requestPath: error?.config?.url,
    requestData: error?.config?.data && JSON.parse(error?.config?.data),
    message: error?.response?.data,
    custom,
    error,
  });

  throw error;
}

export function errorText() {
  // generic message based on future status response
  // if (status === 400) {}
  // if (status === 500) {}
  return `Ingen endring ble lagret, grunnet ukjent feil. Dokflyt er informert om feilen.`;
}
